<template>
  <div v-if="technicianPayment">
    <base-page-heading
      :title="title"
      show-back-button
    >
      <template #secondary-title>
        <b-badge :variant="statusVariant(technicianPayment)">
          {{ status(technicianPayment) }}
        </b-badge>
      </template>
      <template #extra>
        <b-button
          v-if="$can('approve', technicianPayment) && technicianPayment.approved_at === null"
          v-b-tooltip.hover
          variant="primary"
          class="d-none d-md-inline-block"
          title="Approve the Technician Payment"
          @click="approveTechnicianPayment"
        >
          <i class="fa fa-check mr-1" />
          Approve
        </b-button>

        <b-button
          v-if="$can('update', technicianPayment)"
          variant="primary"
          v-b-modal.technician-payment-edit-modal
          class="d-none d-md-inline-block"
          v-b-tooltip.hover
          title="Edit the Payment"
        >
          <i class="fa fa-pencil-alt mr-1" /> Edit
        </b-button>

        <b-button
          variant="outline-secondary"
          v-b-modal.technician-payment-email-modal
          v-b-tooltip.hover
          title="Email the Technician Payment"
        >
          <i class="fa fa-envelope" />
        </b-button>

        <sfs-download-button
          v-b-tooltip.hover
          :filename="filename"
          :file-provider="getPDF"
          title="Download the Technician Payment"
        />

        <b-dropdown
          variant="outline-secondary"
          no-caret
          right
        >
          <template #button-content>
            <i class="fa fa-ellipsis-v" />
          </template>

          <div class="d-md-none">
            <b-dropdown-item
              v-if="$can('approve', technicianPayment) && technicianPayment.approved_at === null"
              @click="approveTechnicianPayment"
            >
              <i class="fa fa-check mr-1" /> Approve
            </b-dropdown-item>

            <b-dropdown-item
              v-if="$can('update', technicianPayment)"
              v-b-modal.technician-payment-edit-modal
            >
              <i class="fa fa-pencil-alt mr-1" /> Edit
            </b-dropdown-item>

            <b-dropdown-divider />
          </div>
          
          <b-dropdown-item
            v-if="$can('destroy', 'TechnicianPayment')"
            @click="destroyTechnicianPayment"
          >
            <i class="fa fa-trash mr-1" />
            Delete Payment
          </b-dropdown-item>

          <b-dropdown-item
            v-if="$can('destroy_with_items', 'TechnicianPayment')"
            @click="destroyTechnicianPaymentWithItems"
          >
            <i class="fa fa-trash mr-1" />
            Delete Payment with Items
          </b-dropdown-item>

          <b-dropdown-item
            v-if="$can('unfinalize', technicianPayment) && technicianPayment.finalized_at"
            @click="unfinalizeTechnicianPayment"
          >
            <i class="fa fa-undo mr-1" />
            Unfinalize
          </b-dropdown-item>

          <b-dropdown-divider
            v-if="$can('destroy', 'TechnicianPayment') || $can('destroy_with_items', 'TechnicianPayment')"
          />

          <b-dropdown-item
            v-if="technicianPayment.has_workers_comp_deduction_item"
            @click="removeWorkersCompDeduction"
          >
            <i class="fa fa-minus-circle mr-1" />
            Remove Insurance
          </b-dropdown-item>

          <b-dropdown-item v-b-modal.communications-list-modal>
            <i class="fa fa-history" />
            Email History
          </b-dropdown-item>

          <b-dropdown-item
            v-if="$can('read', 'Version')"
            :to="{ name: 'versions', query: { model_id: technicianPayment.id, model: 'TechnicianPayment' } }"
          >
            <i class="fa fa-glasses mr-1" />
            Audit Log
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </base-page-heading>

    <div class="content pt-lg-5">
      <b-row class="mb-5">
        <b-col
          md="12"
          lg="6"
        >
          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Period
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ technicianPayment | dateRangeFormat("start_date", "end_date") }}
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Due Date
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ technicianPayment.due_date | dateFormat }}
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Emailed
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="technicianPayment.emailed_at">
                  {{ technicianPayment.emailed_at | dateTimeFormat }}
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Total
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <money-format
                    :value="technicianPayment.balance.amount"
                    :currency-code="technicianPayment.balance.currency"
                    subunits-value
                  />
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-col>

        <b-col
          md="12"
          lg="6"
        >
          <b-row>
            <b-col
              cols="12"
              sm="7"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Technician
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <b-link :to="{ name: 'user', params: { id: user.id } }">
                    {{ user.name }}
                  </b-link>
                </b-col>
              </b-row>

              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Address
                </b-col>
              </b-row>

              <b-row>
                <b-col>
                  <div>{{ user.address }}</div>
                  <div>
                    {{
                      {
                        city: user.city,
                        state: user.state,
                        zip_code: user.zip_code,
                      } | locationFormat
                    }}
                  </div>
                </b-col>
              </b-row>
            </b-col>
            <b-col
              cols="12"
              sm="5"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Email
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <b-link :href="`mailto:${user.email}`">
                    {{ user.email }}
                  </b-link>
                </b-col>
              </b-row>

              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Phone
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <b-link :href="`tel:${user.phone}`">
                    {{ user.phone }}
                  </b-link>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row class="mb-3">
        <b-col lg="12">
          <technician-payment-items-list-filter
            :technician-payment="technicianPayment"
            :initial-filters="filters"
            @filters-applied="applyFilters"
            @data-changed="refreshTable"
          />
          <technician-payment-items-table
            :technician-payment-id="$route.params.id.toString()"
            :append-to-query="false"
            :filters="filters"
            :total="technicianPayment.balance"
            :key="tableKey"
          />
        </b-col>
      </b-row>
    </div>
    <!-- Modals -->
    <technician-payments-edit-modal
      v-if="$can('update', 'TechnicianPayment')"
      v-model="technicianPayment"
    />
    <email-modal
      v-model="technicianPayment"
      :email="technicianPayment.user.email"
      cc_manager
    />
    <communications-list-modal
      :list-provider="$communicationsAPI.getTechnicianPaymentCommunications"
      :reference-id="technicianPayment.id"
      title="Email History"
    />
  </div>
</template>

<script>
import TechnicianPaymentItemsTable from '@/components/admin/technicianPaymentItems/Table.vue';
import TechnicianPaymentItemsListFilter from '@/components/admin/technicianPaymentItems/ListFilter.vue';

import EmailModal from '@/components/admin/technicianPayments/EmailModal.vue';
import TechnicianPaymentsEditModal from '@/components/admin/technicianPayments/EditModal.vue';
import CommunicationsListModal from '@/components/admin/communications/ListModal.vue';

import technicianPaymentsMixin from '@/mixins/technicianPaymentsMixin';
import errorToastMixin from '@/mixins/errorToastMixin';

export default {
  name: 'TechnicianPaymentsShow',
  mixins: [technicianPaymentsMixin, errorToastMixin],
  components: {
    TechnicianPaymentItemsTable,
    TechnicianPaymentItemsListFilter,
    TechnicianPaymentsEditModal,
    EmailModal,
    CommunicationsListModal,
  },
  data() {
    return {
      technicianPayment: null,
      tableKey: Date.now(),
      filters: {
        text: this.text,
      },
    };
  },
  mounted() {
    this.getTechnicianPayment();
  },
  computed: {
    title() {
      return `Technician Payment ${this.technicianPayment.number}`;
    },
    user() {
      return this.technicianPayment.user;
    },
    filename() {
      return `Technician Payment ${this.technicianPayment.number}.pdf`;
    },
  },
  methods: {
    getTechnicianPayment() {
      this.$technicianPaymentsAPI
        .get(this.$route.params.id)
        .then((technicianPayment) => (this.technicianPayment = technicianPayment))
        .catch(() => {
          this.$router.push('/404');
        });
    },
    refreshTable() {
      this.tableKey = Date.now();
    },
    getPDF() {
      return this.$technicianPaymentsAPI.getPDF(this.$route.params.id);
    },
    approveTechnicianPayment() {
      this.$swal({
        text: 'Once approved, this payment will be emailed to the technician',
        preConfirm: () => this.$technicianPaymentsAPI.approve(this.technicianPayment.id),
      }).then((result) => {
        if (result.value) {
          this.technicianPayment = result.value;
        }
        result.timer;
      });
    },
    unfinalizeTechnicianPayment() {
      this.$swal({
        text: 'Are you sure you want to unfinalize the Technician Payment?',
        preConfirm: () => this.$technicianPaymentsAPI.unfinalizeTechnicianPayment(this.technicianPayment.id),
      }).then((result) => {
        if (result.value) {
          this.technicianPayment = result.value;
          this.refreshTable();
        }
        result.timer;
      });
    },
    applyFilters({ filters }) {
      this.filters = Object.assign({}, filters);
    },
    destroyTechnicianPayment() {
      this.$swal({
        text: 'Are you sure you want to delete the Technician Payment? This cannot be undone.',
        preConfirm: () => this.$technicianPaymentsAPI.destroy(this.$route.params.id).catch((error) => {
          let message = 'We\'ve experienced an issue while trying to delete the technician payment, Please try again.';
          if (error.response && error.response.status === 403) {
            message = 'This technician payment cannot be deleted - only payments without items can be deleted.';
          }
          this.toastDanger(message);
          return false;
        }),
      }).then((result) => {
        if (result.value) {
          this.$router.push({ name: 'technician-payments', query: this.$route.query });
        }
        result.timer;
      });
    },
    destroyTechnicianPaymentWithItems() {
      this.$swal({
        text: 'Are you sure you want to delete the Technician Payment with Items? This cannot be undone.',
        preConfirm: () => this.$technicianPaymentsAPI.destroyWithItems(this.$route.params.id).catch((error) => {
          let message = 'We\'ve experienced an issue while trying to delete the technician payment, Please try again.';
          if (error.response && error.response.status === 403) {
            message = 'This technician payment cannot be deleted.';
          }
          this.toastDanger(message);
          return false;
        }),
      }).then((result) => {
        if (result.value) {
          this.$router.push({ name: 'technician-payments', query: this.$route.query });
        }
        result.timer;
      });
    },
    removeWorkersCompDeduction() {
      this.$swal({
        text: 'Are you sure you want to remove the insurance deduction from this payment?',
        preConfirm: () => this.$technicianPaymentsAPI.workers_comp_deduction(this.technicianPayment.id),
      }).then((result) => {
        if (result.value) {
          this.getTechnicianPayment();
          this.refreshTable();
        }
        result.timer;
      });
    },
  },
};
</script>
